import { RefObject } from 'react';
import { NavigateFunction } from 'react-router';
import {
  eventKeyDown,
  eventKeyEnter,
  eventKeyUp,
} from '@legacy/constants/keyboardKey';

const highlightSelectedElement = (element: Element) =>
  element.setAttribute('aria-selected', 'true');

const scrollToSelectedElement = (element: Element) =>
  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  });

export const blurSelectElement = (element: Element) =>
  element?.setAttribute('aria-selected', 'false');

export const reloadKeyboardSelectedElement = (
  selectedElement: Element,
  inputElement: RefObject<HTMLInputElement | null>,
) => {
  if (selectedElement) {
    highlightSelectedElement(selectedElement);
    scrollToSelectedElement(selectedElement);
    inputElement.current?.setAttribute(
      'aria-activedescendant',
      selectedElement.id,
    );
  } else {
    inputElement.current?.removeAttribute('aria-activedescendant');
  }
};

interface KeyboardActionProps {
  event: any;
  setSelectItemIndex: React.Dispatch<React.SetStateAction<number>>;
  selectItemIndex: number;
  focusableElements: Element[];
  onItemSelect?: () => void;
  navigate?: NavigateFunction;
  // Then we should not be able to navigate to the last item when
  // pressing the up arrow key from the first item and vice versa.
  // Ideally, we should be able to navigate to the last item, but
  // then we be able to load the last page from the api to show the last item.
  hasLazyLoad?: boolean;
}

export const keyboardAction = ({
  event,
  setSelectItemIndex,
  selectItemIndex,
  onItemSelect,
  navigate,
  focusableElements,
  hasLazyLoad = false,
}: KeyboardActionProps): boolean => {
  const focusableElementsTagList = focusableElements?.map((el) => el.tagName);
  const firstButtonIndex = focusableElementsTagList.indexOf('BUTTON');
  const lastButtonIndex = focusableElementsTagList.lastIndexOf('BUTTON');
  const focusableLength = focusableElements.length - 1;
  const currentSelectedItem = focusableElements[selectItemIndex] as HTMLElement;
  const { key } = event;
  if (key === eventKeyUp && event.ctrlKey) {
    if (selectItemIndex === -1 || selectItemIndex === 0) {
      if (hasLazyLoad) {
        return false;
      }
      setSelectItemIndex(lastButtonIndex);
    } else {
      const previousButtonIndex = focusableElementsTagList.lastIndexOf(
        'BUTTON',
        selectItemIndex - 1,
      );
      setSelectItemIndex(previousButtonIndex);
    }
  } else if (key === eventKeyDown && event.ctrlKey) {
    if (
      selectItemIndex === lastButtonIndex ||
      selectItemIndex > lastButtonIndex
    ) {
      if (hasLazyLoad) {
        return false;
      }

      setSelectItemIndex(firstButtonIndex);
    } else {
      const nextButtonIndex = focusableElementsTagList.indexOf(
        'BUTTON',
        selectItemIndex + 1,
      );
      setSelectItemIndex(nextButtonIndex);
    }
  } else if (key === eventKeyUp) {
    event.preventDefault();
    if (selectItemIndex === -1 || selectItemIndex === 0) {
      if (hasLazyLoad) {
        return false;
      }

      setSelectItemIndex(focusableLength);
    } else {
      setSelectItemIndex((prev) => prev - 1);
    }
  } else if (key === eventKeyDown) {
    event.preventDefault();
    if (selectItemIndex === focusableLength) {
      if (hasLazyLoad) {
        return false;
      }

      setSelectItemIndex(0);
    } else {
      setSelectItemIndex((prev) => prev + 1);
    }
  } else if (key === eventKeyEnter) {
    event.preventDefault();
    if (currentSelectedItem?.nodeName === 'A' && navigate && onItemSelect) {
      navigate(currentSelectedItem?.getAttribute('href') ?? '');
      setSelectItemIndex(-1);
      onItemSelect();
    } else if (
      !event.ctrlKey &&
      (currentSelectedItem?.nodeName === 'BUTTON' ||
        currentSelectedItem?.getAttribute('role') === 'button')
    ) {
      currentSelectedItem.click();
      return false;
    }
  } else if (!event.ctrlKey) {
    setSelectItemIndex(-1);
  }

  return true;
};
